import { useCallback, useEffect, useRef, KeyboardEvent } from 'react';
import useLocationSearchHook from 'hooks/use-location-search';
import { useSearchBarTracking } from 'hooks/use-search-bar-tracking';
import { useFeaturesContext } from 'contexts';
import { SEARCH_OPTION_AGENTS, SEARCH_OPTION_BUY, SearchOptions } from '@tenants/types/theme';

import type SearchPrediction from 'data/search-predictions';
import type { SearchAgentPrediction } from 'components/suggested-location-dropdown';

//#region Types
export interface UseLocationSearchOptions {
  /** Current search query */
  locationQuery: string;
  /** Handler to update the search query */
  setLocationQuery: (query: string) => void;
  /** Whether the dropdown is currently active */
  isLocationDropdownActive: boolean;
  /** Handler to toggle dropdown visibility */
  setIsLocationDropdownActive: (isActive: boolean) => void;
  /** Handler called when a search prediction is clicked */
  onClick: (searchPrediction: SearchPrediction) => void;
  /** Handler to set the active prediction */
  setActivePrediction?: (searchPrediction: SearchPrediction) => void;
  /** Search option type (buy, rent, sold, agents) */
  searchOption?: SearchOptions;
  /** Whether this is an area search (closes dropdown on outside click) */
  isAreaSearch?: boolean;
  /** Currently chosen area tag */
  chosenArea?: string;
  /** Handler to update chosen area */
  setChosenArea?: (area: string) => void;
  /** Handler for search icon click with predictions */
  onSearchIconClick?: (searchPredictions?: SearchPrediction[], query?: string) => void;
  /** Analytics handler for search bar click */
  onSearchBarClickAnalytics?: () => void;
}

export interface UseLocationSearchResult {
  // State from location search hook
  isLoadingSearchPredictions: boolean;
  searchPredictions: SearchPrediction[];
  searchAgentPredictions: SearchAgentPrediction[];
  isTyping: boolean;
  
  // Derived state
  isAgentSearch: boolean;
  showMiddleIcon: boolean;
  
  // Handlers
  handleInputChange: (value: string) => void;
  handleInputClick: () => void;
  handleInputFocus: () => void;
  handleInputBlur: () => void;
  handleSearchIconClick: () => Promise<void>;
  handleClearClick: () => void;
  handleSuggestedLocationClick: (searchPrediction: SearchPrediction) => void;
  handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  handleClickOutside: () => void;
}
//#endregion

/**
 * useLocationSearch - Hook for managing location search state and behavior
 * 
 * This hook encapsulates all the logic for location search including:
 * - Location predictions fetching
 * - Search tracking analytics
 * - Input event handling
 * - Prediction click handling
 * 
 * @example
 * ```tsx
 * const {
 *   searchPredictions,
 *   handleInputChange,
 *   handleSearchIconClick,
 * } = useLocationSearch({
 *   locationQuery,
 *   setLocationQuery,
 *   isLocationDropdownActive,
 *   setIsLocationDropdownActive,
 *   onClick: handlePredictionClick,
 * });
 * ```
 */
export function useLocationSearch({
  locationQuery,
  setLocationQuery,
  setIsLocationDropdownActive,
  onClick,
  setActivePrediction = () => undefined,
  searchOption = SEARCH_OPTION_BUY,
  chosenArea,
  setChosenArea,
  onSearchIconClick,
  onSearchBarClickAnalytics,
}: UseLocationSearchOptions): UseLocationSearchResult {
  const { features } = useFeaturesContext();
  
  const {
    isLoadingSearchPredictions,
    searchPredictions,
    searchAgentPredictions,
    isTyping,
    waitForPredictionsToLoad,
  } = useLocationSearchHook({
    queryString: locationQuery,
    useUsListings: features.useUsListings,
    searchOptions: searchOption,
  });

  const isAgentSearch = searchOption === SEARCH_OPTION_AGENTS;

  const { handleFocus, handleInput, handleBlur, markSearchAsSubmitted } = useSearchBarTracking({
    textInputRef: null,
    locationQuery,
    isHomePage: false,
    onSearchPage: false,
    isInPanel: false,
  });

  // Keep a ref to current predictions for async access
  const searchPredictionsRef = useRef<SearchPrediction[]>([]);
  useEffect(() => {
    searchPredictionsRef.current = searchPredictions;
  }, [searchPredictions]);

  // Handlers
  const handleInputChange = useCallback((value: string) => {
    setLocationQuery(value);
    handleInput(value);
  }, [setLocationQuery, handleInput]);

  const handleInputClick = useCallback(() => {
    onSearchBarClickAnalytics?.();
    setIsLocationDropdownActive(true);
  }, [onSearchBarClickAnalytics, setIsLocationDropdownActive]);

  const handleInputFocus = useCallback(() => {
    handleFocus();
  }, [handleFocus]);

  const handleInputBlur = useCallback(() => {
    handleBlur();
  }, [handleBlur]);

  const handleClearClick = useCallback(() => {
    setLocationQuery('');
    if (setChosenArea) {
      setChosenArea('');
    }
    handleInput('');
  }, [setLocationQuery, setChosenArea, handleInput]);

  const handleSearchIconClick = useCallback(async () => {
    await waitForPredictionsToLoad();

    // Wait for predictions to be available (with timeout)
    const waitUntilPredictionsAvailable = async (timeoutMs = 2000) => {
      if (searchPredictionsRef.current.length > 0) return;
      await new Promise<void>(resolve => {
        const start = Date.now();
        const interval = setInterval(() => {
          const hasResults = searchPredictionsRef.current.length > 0;
          const timedOut = Date.now() - start >= timeoutMs;
          if (hasResults || timedOut) {
            clearInterval(interval);
            resolve();
          }
        }, 50);
      });
    };

    await waitUntilPredictionsAvailable();

    markSearchAsSubmitted();
    if (onSearchIconClick) {
      onSearchIconClick(searchPredictionsRef.current, locationQuery);
    }
  }, [waitForPredictionsToLoad, markSearchAsSubmitted, onSearchIconClick, locationQuery]);

  const handleSuggestedLocationClick = useCallback((searchPrediction: SearchPrediction) => {
    markSearchAsSubmitted();
    setActivePrediction(searchPrediction);
    onClick(searchPrediction);
  }, [markSearchAsSubmitted, setActivePrediction, onClick]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearchIconClick();
    }
  }, [handleSearchIconClick]);

  const handleClickOutside = useCallback(() => {
    setIsLocationDropdownActive(false);
  }, [setIsLocationDropdownActive]);

  // Show middle icon (clear button) when there's input value and no chosen area tag
  const showMiddleIcon = !!(locationQuery && !chosenArea);

  return {
    // State from location search hook
    isLoadingSearchPredictions,
    searchPredictions,
    searchAgentPredictions,
    isTyping,
    
    // Derived state
    isAgentSearch,
    showMiddleIcon,
    
    // Handlers
    handleInputChange,
    handleInputClick,
    handleInputFocus,
    handleInputBlur,
    handleSearchIconClick,
    handleClearClick,
    handleSuggestedLocationClick,
    handleKeyDown,
    handleClickOutside,
  };
}
